import { useMemo, useState } from "react";
import SimpleTable from "../../../shared/SimpleTable";

const cardStyle = {
    backgroundColor: 'var(--white)',
    borderRadius: '.6rem',
    boxShadow: 'rgba(0, 0, 0, 0.05) 0px 0px 0px 1px',
    padding: '1rem 1.2rem',
    marginBottom: '1rem',
};

const sectionTitleStyle = {
    fontSize: '1rem',
    fontWeight: 600,
    paddingBottom: '.6rem',
    color: 'var(--darkerGrey)',
};

const statLabelStyle = {
    fontSize: '.75rem',
    color: 'var(--grey)',
    textTransform: 'uppercase',
    letterSpacing: '.03em',
    marginBottom: '.25rem',
};

const CONTRACT_TYPES = {
    I: 'I — Incurred and Paid',
    P: 'P — Paid',
    IRP: 'IRP — Incurred and Paid with a Run In Period Limitation',
    IRPD: 'IRPD — Incurred and Paid with a Run in Period and Dollar Limitation',
    IE: 'IE — Incurred with Extension',
};

const BENEFIT_PLAN_GROUPS = [
    { key: 'MMSpecBenefitPlans', label: 'Major Medical Benefit Plans' },
    { key: 'DSpecBenefitPlans', label: 'Dental Benefit Plans' },
    { key: 'RSpecBenefitPlans', label: 'Prescription Benefit Plans' },
    { key: 'RXSpecBenefitPlans', label: 'Prescription Benefit Plans' },
    { key: 'PSpecBenefitPlans', label: 'Prescription Benefit Plans' },
    { key: 'VSpecBenefitPlans', label: 'Vision Benefit Plans' },
    { key: 'DISpecBenefitPlans', label: 'Disability Benefit Plans' },
    { key: 'SSpecBenefitPlans', label: 'Surcharge Benefit Plans' },
];

export const asJvArray = (value) => {
    if (value == null || value === '') return [];
    if (Array.isArray(value)) return value.filter((item) => item != null);
    if (typeof value === 'object') return [value];
    return [];
};

const isBlank = (value) => value === undefined || value === null || value === '';

const formatYn = (value) => {
    if (isBlank(value)) return null;
    const v = String(value).trim().toUpperCase();
    if (['Y', 'YES', 'TRUE', '1'].includes(v)) return 'Yes';
    if (['N', 'NO', 'FALSE', '0'].includes(v)) return 'No';
    return String(value);
};

const formatStatus = (value) => {
    if (isBlank(value)) return null;
    const v = String(value).trim().toUpperCase();
    if (v === 'A' || v === 'ACTIVE') return 'Active';
    if (v === 'I' || v === 'INACTIVE') return 'Inactive';
    return String(value);
};

const formatCoverageType = (value) => {
    if (isBlank(value)) return null;
    const v = String(value).trim().toUpperCase();
    if (v === 'I' || v === 'INDIVIDUAL') return 'Individual';
    if (v === 'F' || v === 'FAMILY') return 'Family';
    return String(value);
};

const formatContractType = (value) => {
    if (isBlank(value)) return null;
    const key = String(value).trim().toUpperCase();
    return CONTRACT_TYPES[key] || String(value);
};

const formatAmount = (value) => {
    if (isBlank(value)) return null;
    const n = Number(String(value).replace(/,/g, ''));
    if (!Number.isFinite(n)) return String(value);
    return n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
};

const formatPercent = (value) => {
    if (isBlank(value)) return null;
    const n = Number(String(value).replace(/%/g, ''));
    if (!Number.isFinite(n)) return String(value);
    return `${n}%`;
};

const formatHexColor = (value) => {
    if (isBlank(value)) return null;
    const hex = String(value).replace(/^#/, '');
    return /^[0-9A-Fa-f]{3,8}$/.test(hex) ? `#${hex}` : String(value);
};

const PRODUCT_LABELS = {
    ALL: 'All products',
    MM: 'Medical',
    MD: 'Medical',
    MEDICAL: 'Medical',
    RX: 'Prescription',
    RXS: 'Prescription',
    PRESCRIPTION: 'Prescription',
    DN: 'Dental',
    DENTAL: 'Dental',
    VS: 'Vision',
    VIS: 'Vision',
    VISION: 'Vision',
    DI: 'Disability',
    DISABILITY: 'Disability',
    SC: 'Surcharge',
    SURCHARGE: 'Surcharge',
};

const formatProduct = (value) => {
    const key = String(value || '').trim().toUpperCase();
    return PRODUCT_LABELS[key] || String(value || '').trim();
};

const joinList = (items) => {
    if (!items.length) return '';
    if (items.length === 1) return items[0];
    if (items.length === 2) return `${items[0]} and ${items[1]}`;
    return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;
};

const formatIncurredLogic = (value) => {
    if (isBlank(value)) return null;
    const v = String(value).trim();
    if (/original/i.test(v)) return 'Uses the original date of service on the claim';
    if (/adjust/i.test(v)) return 'Uses the adjusted incurred date on the claim';
    return v;
};

const parseJvDate = (value) => {
    if (isBlank(value)) return null;
    const s = String(value).trim();
    const mdy = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (mdy) return new Date(Number(mdy[3]), Number(mdy[1]) - 1, Number(mdy[2]));
    const ymd = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (ymd) return new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3]));
    const iso = new Date(s);
    return Number.isNaN(iso.getTime()) ? null : iso;
};

const formatDate = (value) => {
    const d = parseJvDate(value);
    if (!d) return isBlank(value) ? null : String(value);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const monthSpan = (from, thru) => {
    const a = parseJvDate(from);
    const b = parseJvDate(thru);
    if (!a || !b) return null;
    const months = (b.getFullYear() - a.getFullYear()) * 12 + (b.getMonth() - a.getMonth()) + 1;
    if (months < 1) return null;
    return months === 1 ? '1 month' : `${months} months`;
};

const versionYear = (spec) => {
    const d = parseJvDate(spec?.EffectiveDate);
    if (d) return String(d.getFullYear());
    const id = String(spec?.StopLossContractID || spec?.StopLossContractName || '');
    const match = id.match(/(?:19|20)\d{2}$/);
    return match ? match[0] : null;
};

const Badge = ({ children, color = 'var(--darkerGrey)', bg = 'var(--lightestGrey)' }) => (
    <span style={{
        display: 'inline-block',
        padding: '.15rem .55rem',
        borderRadius: '.25rem',
        fontSize: '.75rem',
        fontWeight: 550,
        backgroundColor: bg,
        color,
        whiteSpace: 'nowrap',
    }}>
        {children}
    </span>
);

const Card = ({ title, children, defaultOpen = true, count }) => {
    const [open, setOpen] = useState(defaultOpen);
    if (children == null) return null;
    return (
        <div style={cardStyle}>
            <div
                style={{
                    ...sectionTitleStyle,
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: open ? '.4rem' : 0,
                }}
                onClick={() => setOpen(!open)}
            >
                <span style={{ display: 'flex', alignItems: 'center', gap: '.5rem' }}>
                    {title}
                    {count !== undefined && (
                        <span style={{
                            fontSize: '.75rem',
                            color: 'var(--grey)',
                            backgroundColor: 'var(--lightestGrey)',
                            borderRadius: '1rem',
                            padding: '0 .5rem',
                            fontWeight: 500,
                        }}>
                            {count}
                        </span>
                    )}
                </span>
                <span style={{ fontSize: '.8rem', color: 'var(--grey)' }}>{open ? '▾' : '▸'}</span>
            </div>
            {open && children}
        </div>
    );
};

const Fact = ({ label, value }) => {
    if (isBlank(value) && value !== 0) return null;
    return (
        <div>
            <div style={statLabelStyle}>{label}</div>
            <div style={{ fontSize: '.95rem', fontWeight: 600, color: 'var(--darkerGrey)', wordBreak: 'break-word' }}>
                {String(value)}
            </div>
        </div>
    );
};

const Stat = ({ label, value, large, hint }) => {
    const missing = value == null || value === '' || typeof value === 'object';
    return (
        <div>
            <div style={statLabelStyle}>{label}</div>
            <div style={{
                fontSize: large ? '1.45rem' : '1.15rem',
                fontWeight: 650,
                color: missing ? 'var(--grey)' : 'var(--darkerGrey)',
                letterSpacing: '-.02em',
            }}>
                {missing ? '—' : String(value)}
            </div>
            {hint && (
                <div style={{ fontSize: '.78rem', color: 'var(--grey)', paddingTop: '.2rem', lineHeight: 1.35 }}>
                    {hint}
                </div>
            )}
        </div>
    );
};

const PeriodCard = ({ label, help, from, thru }) => {
    if (isBlank(from) && isBlank(thru)) return null;
    const span = monthSpan(from, thru);
    return (
        <div style={{
            ...cardStyle,
            marginBottom: 0,
            backgroundColor: 'var(--white)',
            boxShadow: 'none',
            border: '1px solid var(--lightGrey)',
            padding: '1rem 1.15rem',
        }}>
            <div style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--darkerGrey)' }}>{label}</div>
            {help && (
                <div style={{ color: 'var(--darkGrey)', fontSize: '.9rem', padding: '.2rem 0 .65rem' }}>{help}</div>
            )}
            <div style={{ fontSize: '1.35rem', fontWeight: 700, color: 'var(--darkerGrey)', letterSpacing: '-.02em' }}>
                {formatDate(from) || '—'}
            </div>
            <div style={{ color: 'var(--grey)', fontSize: '.85rem', padding: '.15rem 0' }}>through</div>
            <div style={{ fontSize: '1.35rem', fontWeight: 700, color: 'var(--darkerGrey)', letterSpacing: '-.02em' }}>
                {formatDate(thru) || '—'}
            </div>
            {span && (
                <div style={{ color: 'var(--darkGrey)', fontSize: '.85rem', paddingTop: '.55rem' }}>
                    {span.replace(/ months?$/, '-month window')}
                </div>
            )}
        </div>
    );
};

const DetailGrid = ({ items }) => {
    const rows = items.filter(([, value]) => !isBlank(value) && typeof value !== 'object');
    if (!rows.length) return null;
    return (
        <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
            gap: '1rem',
        }}>
            {rows.map(([label, value]) => (
                <Fact key={label} label={label} value={value} />
            ))}
        </div>
    );
};

const ColorSwatch = ({ value }) => {
    const color = formatHexColor(value);
    if (!color) return null;
    return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '.5rem' }}>
            <span style={{
                width: '1.1rem',
                height: '1.1rem',
                borderRadius: '.25rem',
                backgroundColor: color,
                border: '1px solid var(--lightGrey)',
                display: 'inline-block',
            }} />
            {color}
        </span>
    );
};

const listProducts = (spec) => {
    const fromFlags = [
        ['All products', spec?.AllProducts],
        ['Medical', spec?.Medical],
        ['Dental', spec?.Dental],
        ['Prescription', spec?.Prescription],
        ['Vision', spec?.Vision],
        ['Disability', spec?.Disability],
        ['Surcharge', spec?.Surcharge],
    ].filter(([, flag]) => formatYn(flag) === 'Yes').map(([label]) => label);

    if (fromFlags.length) return fromFlags;
    if (isBlank(spec?.CoveredProducts)) return [];
    return String(spec.CoveredProducts)
        .split(/[,|;/]+/)
        .map((part) => formatProduct(part))
        .filter(Boolean);
};

const ProductChips = ({ products }) => {
    if (!products?.length) return null;
    return (
        <div style={{ display: 'flex', gap: '.5rem', flexWrap: 'wrap' }}>
            {products.map((label) => (
                <Badge key={label}>{label}</Badge>
            ))}
        </div>
    );
};

const pickSpecDeductible = (spec, tracker) => {
    const candidates = [
        tracker?.SpecificAmount,
        spec?.SpecificAmount,
        spec?.SpecDeductibleAmt,
    ];
    return candidates.find((value) => !isBlank(value)) ?? null;
};

const ContractViewer = ({ contract }) => {
    const response = contract?.response || {};
    const spec = response.SpecStopLossContract || contract?.summary || {};
    const tracker = contract?.tracker || {};
    const status = formatStatus(spec.StopLossContractStatus);
    const coverage = formatCoverageType(spec.CoverageType);
    const products = listProducts(spec);
    const productPhrase = joinList(products);
    const year = versionYear(spec);
    const incurredLogic = formatIncurredLogic(spec.AdjClmIncurredDtFlag);
    const whose = coverage === 'Family' ? "a family's" : "an individual member's";
    const covered = productPhrase ? `${productPhrase.toLowerCase()} ` : '';
    const specDeductibleRaw = pickSpecDeductible(spec, tracker);
    const specDeductible = formatAmount(specDeductibleRaw);
    const appliedToSpec = formatAmount(tracker.AmountAppliedToSpecDeductible);
    const pctOfSpec = formatPercent(tracker['PercentageOfSpecDeductibleMetAsOf-PercentageMetDate']);
    const specN = isBlank(specDeductibleRaw) ? NaN : Number(String(specDeductibleRaw).replace(/[$,]/g, ''));
    const appliedN = isBlank(tracker.AmountAppliedToSpecDeductible) ? NaN : Number(String(tracker.AmountAppliedToSpecDeductible).replace(/[$,]/g, ''));
    const remainingN = Number.isFinite(specN) && Number.isFinite(appliedN) ? Math.max(0, specN - appliedN) : null;
    const remainingAmt = remainingN == null ? null : formatAmount(remainingN);
    const specMet = remainingN === 0 && specN > 0;
    const deductiblePhrase = specDeductible
        ? `the ${specDeductible} deductible`
        : 'the deductible (amount missing from the reinsurance tracker)';
    const summary = [
        `This ${year ? `${year} ` : ''}stop-loss contract pays after ${whose} ${covered}claims exceed ${deductiblePhrase}.`,
        formatDate(spec.EffectiveDate) ? `It took effect ${formatDate(spec.EffectiveDate)}.` : '',
    ].filter(Boolean).join(' ');

    const benefitPlanTables = BENEFIT_PLAN_GROUPS
        .map((group) => ({ ...group, rows: asJvArray(response[group.key]) }))
        .filter((group) => group.rows.length);

    const networks = asJvArray(response.SpecNetworkCodes);
    const statusCodes = asJvArray(response.SpecCntrStatusCodes);
    const triggerDiags = asJvArray(response.TriggDiagCat);
    const advFundCats = asJvArray(response.SpecAdvFundCat);
    const encntCats = asJvArray(response.SpecEncntAmtCat);

    const contractDetails = [
        ['Carrier', spec.Carrier],
        ['Contract Term', [spec.ContractTerm1, spec.ContractTerm2].filter((v) => !isBlank(v)).join(' / ') || null],
        ['Run In Incurred Date', formatDate(spec.RunInIncurredDate)],
        ['Run In Dollar Limitation', formatAmount(spec.RunInDollarLimitationAmt)],
        ['Extension Paid Date', formatDate(spec.ExtensionPaidDate)],
        ['Apply Contract Period Maximum', formatYn(spec.ApplyContractPeriodMaximum)],
        ['Aggregating Specific Applies', formatYn(spec.AggrSpecificApplies)],
        ['Aggregating Specific Deductible', formatAmount(spec.AggrSpecificDeductibleAmt)],
        ['Aggregate Contract (1)', spec.AggrContractName1],
        ['Aggregate Contract (2)', spec.AggrContractName2],
        ['Advanced Funding Required', formatYn(spec.AdvFundingRequired)],
        ['Letter Sent to Provider', spec.LetterSenttoProvider],
        ['Split Threshold Claims', formatYn(spec.SplitThresholdClm)],
        ['Payments Report Form', spec.FormatName],
        ['Payments Vendor', spec.VendorName],
        ['Print PHI on Payments Report', formatYn(spec.PHIForPaymentReport)],
        ['Post-Payments Report Form', spec.PostPaymentsFormatName],
        ['Post-Payments Vendor', spec.PostPaymentsVendorName],
    ];

    const alertDetails = [
        ['Specific Deductible Met Alert', formatYn(spec.WarnAmtMetAlert)],
        ['Alert Message', spec.WarnAmtMetAlertMsg],
        ['Warning Amount Exceeded Alert', formatYn(spec.WarnAmtExceedAlert)],
        ['Warning Amount Exceeded Message', spec.WarnAmtExceedAlertMsg],
        ['Contract Period Maximum Met Alert', formatYn(spec.ContrPerMaxMetAlert)],
        ['Contract Period Maximum Message', spec.ContrPerMaxMetAlertMsg],
    ];

    const hasContractDetails = contractDetails.some(([, value]) => !isBlank(value) && typeof value !== 'object');
    const hasAlertColor = !!formatHexColor(spec.StopLossAlertColor);
    const hasAlerts = hasAlertColor || alertDetails.some(([, value]) => !isBlank(value));
    const hasPeriods = !isBlank(spec.IncurredFromDate) || !isBlank(spec.IncurredThruDate)
        || !isBlank(spec.PaidFromDate) || !isBlank(spec.PaidThruDate);

    const incurredSpan = monthSpan(spec.IncurredFromDate, spec.IncurredThruDate);
    const paidSpan = monthSpan(spec.PaidFromDate, spec.PaidThruDate);
    const termHint = incurredSpan && paidSpan
        ? `${incurredSpan.replace(' months', '')} / ${paidSpan.replace(' months', '')} months`
        : null;

    return (
        <>
            <div style={{ ...cardStyle, padding: '1.25rem 1.4rem' }}>
                <div style={{
                    fontSize: '.72rem',
                    fontWeight: 600,
                    letterSpacing: '.04em',
                    textTransform: 'uppercase',
                    color: 'var(--grey)',
                    paddingBottom: '.45rem',
                }}>
                    The contract
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem', flexWrap: 'wrap' }}>
                    <p className="title" style={{ margin: 0, padding: 0 }}>
                        {year ? `${year} · ${spec.StopLossContractName || contract?.name || 'Stop-loss'}` : (spec.StopLossContractName || contract?.name || 'Stop-loss contract')}
                    </p>
                    {status && <Badge>{status}</Badge>}
                    {coverage && (
                        <Badge>
                            {coverage === 'Individual' ? 'Per member' : coverage}
                        </Badge>
                    )}
                </div>
                <p style={{ paddingTop: '.55rem', fontSize: '.95rem', lineHeight: 1.45, maxWidth: '40rem', color: 'var(--darkerGrey)' }}>
                    {specMet
                        ? `Specific stop-loss is met for ${whose} ${covered}claims.`
                        : remainingAmt
                            ? `${remainingAmt} left before specific stop-loss applies (${deductiblePhrase}).`
                            : summary}
                </p>
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                    gap: '1rem 1.5rem',
                    paddingTop: '1rem',
                    maxWidth: '46rem',
                }}>
                    <Stat
                        label="Spec deductible"
                        value={specDeductible}
                        large
                        hint={contract?.trackerError ? `Tracker: ${contract.trackerError}` : null}
                    />
                    <Stat label="Applied to spec" value={appliedToSpec} large />
                    <Stat
                        label={specMet ? 'Stop-loss' : 'Left before spec'}
                        value={specMet ? 'Met' : remainingAmt}
                        large
                    />
                    <Stat label="% of spec met" value={pctOfSpec} />
                </div>
                {spec.Description && (
                    <p className="sub-title" style={{ paddingTop: '.45rem', fontSize: '.95rem', maxWidth: '52rem' }}>
                        {spec.Description}
                    </p>
                )}
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
                    gap: '1rem 1.25rem',
                    paddingTop: '1.1rem',
                }}>
                    <Fact label="Contract name" value={spec.StopLossContractName || contract?.name} />
                    <Fact label="Version ID" value={spec.StopLossContractID} />
                    <Fact label="How dates are counted" value={incurredLogic} />
                    {termHint && <Fact label="Term (incurred / paid)" value={termHint} />}
                    <Fact label="Contract type" value={formatContractType(spec.ContractType)} />
                </div>
                {!!products.length && (
                    <div style={{ paddingTop: '1rem' }}>
                        <div style={{ ...statLabelStyle, marginBottom: '.45rem' }}>Covered products</div>
                        <ProductChips products={products} />
                    </div>
                )}
            </div>

            {hasPeriods && (
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                    gap: '1rem',
                    marginBottom: '1rem',
                }}>
                    <PeriodCard
                        label="Claims must be incurred"
                        help="The date of service on the claim"
                        from={spec.IncurredFromDate}
                        thru={spec.IncurredThruDate}
                    />
                    <PeriodCard
                        label="Claims must be paid"
                        help="The date the claim is paid"
                        from={spec.PaidFromDate}
                        thru={spec.PaidThruDate}
                    />
                </div>
            )}

            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                gap: '1rem',
                marginBottom: '1rem',
            }}>
                <Stat label="Warning amount" value={formatAmount(spec.WarningAmt)} />
                <Stat label="Warning percent" value={formatPercent(spec.WarningPercent)} />
                <Stat label="Max paid out / year" value={formatAmount(spec.MaxPaidOutPerYearAmt)} />
            </div>

            {hasContractDetails && (
                <Card title="Contract details">
                    <DetailGrid items={contractDetails} />
                </Card>
            )}

            {benefitPlanTables.map((group) => (
                <Card key={group.key} title={group.label} count={group.rows.length}>
                    <SimpleTable
                        disableHeaderFormatting={true}
                        columns={[
                            { label: 'BenefitPlan', attribute: 'BenefitPlan' },
                            { label: 'Product', attribute: 'Product' },
                        ]}
                        rows={group.rows}
                    />
                </Card>
            ))}

            {!!networks.length && (
                <Card title="Networks" count={networks.length}>
                    <SimpleTable
                        disableHeaderFormatting={true}
                        columns={[
                            { label: 'Network', attribute: 'NetworkCode' },
                            { label: 'Net Pay % Included', attribute: 'NetPayPercent' },
                        ]}
                        rows={networks}
                    />
                </Card>
            )}

            {hasAlerts && (
                <Card title="Member alerts" defaultOpen={false}>
                    {hasAlertColor && (
                        <div style={{ paddingBottom: '.75rem' }}>
                            <div style={statLabelStyle}>Stop Loss Alert Color</div>
                            <ColorSwatch value={spec.StopLossAlertColor} />
                        </div>
                    )}
                    <DetailGrid items={alertDetails} />
                </Card>
            )}

            {!!triggerDiags.length && (
                <Card title="Trigger diagnosis categories" count={triggerDiags.length} defaultOpen={false}>
                    <SimpleTable
                        disableHeaderFormatting={true}
                        columns={[
                            { label: 'Category', attribute: 'TriggerDiagCategory' },
                            { label: 'Internal ID', attribute: 'InternalID' },
                        ]}
                        rows={triggerDiags}
                    />
                </Card>
            )}

            {!!advFundCats.length && (
                <Card title="Advanced funding exempt categories" count={advFundCats.length} defaultOpen={false}>
                    <SimpleTable
                        disableHeaderFormatting={true}
                        columns={[{ label: 'Category', attribute: 'AdvFundExemptCat' }]}
                        rows={advFundCats}
                    />
                </Card>
            )}

            {!!encntCats.length && (
                <Card title="Encounter amount categories excluded" count={encntCats.length} defaultOpen={false}>
                    <SimpleTable
                        disableHeaderFormatting={true}
                        columns={[{ label: 'Category', attribute: 'CategoriesExcluded' }]}
                        rows={encntCats}
                    />
                </Card>
            )}

            {!!statusCodes.length && (
                <Card title="Status codes included" count={statusCodes.length} defaultOpen={false}>
                    <SimpleTable
                        disableHeaderFormatting={true}
                        columns={[
                            { label: 'Status Code', attribute: 'StatusCode' },
                            { label: 'Description', attribute: 'StatusDesc' },
                            { label: 'Color', attribute: 'CodeColor' },
                            { label: 'Emp / Dep', attribute: 'EmpDep' },
                        ]}
                        rows={statusCodes}
                    />
                </Card>
            )}
        </>
    );
};

const contractSpec = (contract) => contract?.response?.SpecStopLossContract || contract?.summary || {};

const VersionSelect = ({ contracts, selectedIndex, onSelect }) => (
    <div style={{ marginBottom: '1.2rem' }}>
        <p style={{ ...sectionTitleStyle, paddingBottom: '.55rem' }}>Contract year</p>
        <div style={{ display: 'flex', gap: '.55rem', flexWrap: 'wrap' }}>
            {contracts.map((contract, i) => {
                const spec = contractSpec(contract);
                const selected = i === selectedIndex;
                const year = versionYear(spec) || spec.StopLossContractID || `Version ${i + 1}`;
                const status = formatStatus(spec.StopLossContractStatus);
                return (
                    <button
                        key={spec.StopLossContractID || spec.EffectiveDate || i}
                        type="button"
                        onClick={() => onSelect(i)}
                        style={{
                            textAlign: 'left',
                            cursor: 'pointer',
                            border: selected ? '1px solid var(--darkerGrey)' : '1px solid var(--lightGrey)',
                            background: selected ? 'var(--lightestGrey)' : 'var(--white)',
                            borderRadius: '.5rem',
                            padding: '.55rem .9rem',
                            minWidth: '7.25rem',
                        }}
                    >
                        <div style={{ fontWeight: 700, fontSize: '1.1rem', color: 'var(--darkerGrey)' }}>{year}</div>
                        <div style={{ fontSize: '.8rem', color: 'var(--grey)', paddingTop: '.15rem' }}>
                            {[spec.StopLossContractID, status].filter(Boolean).join(' · ')}
                        </div>
                    </button>
                );
            })}
        </div>
    </div>
);

const StopLossContractDetail = ({ stopLoss, assignedName }) => {
    const contracts = stopLoss?.contracts || [];
    const names = stopLoss?.names || (assignedName ? [assignedName] : []);
    const [selectedIndex, setSelectedIndex] = useState(0);

    const selected = useMemo(
        () => contracts[selectedIndex] || contracts[0],
        [contracts, selectedIndex]
    );

    if (!names.length && !contracts.length) {
        return (
            <div style={cardStyle}>
                <p style={sectionTitleStyle}>Specific Stop Loss Contract</p>
                <p style={{ color: 'var(--grey)' }}>No stop loss contract is assigned on this client or its departments.</p>
            </div>
        );
    }

    return (
        <div>
            {!!stopLoss?.errors?.length && (
                <p className="danger" style={{ paddingBottom: '1rem' }}>
                    Reinsurance tracker had a problem, so some amounts may be missing: {stopLoss.errors.map((err) => err.message).filter(Boolean).join(' · ')}
                </p>
            )}
            {!contracts.length ? (
                <div style={cardStyle}>
                    <p style={sectionTitleStyle}>Specific Stop Loss Contract</p>
                    <p>Assigned contract: <strong>{names.join(', ')}</strong></p>
                    <p style={{ color: 'var(--grey)', paddingTop: '.5rem' }}>Could not load contract details from Javelina Reinsurance.</p>
                </div>
            ) : (
                <>
                    {contracts.length > 1 && (
                        <VersionSelect
                            contracts={contracts}
                            selectedIndex={selectedIndex}
                            onSelect={setSelectedIndex}
                        />
                    )}
                    <ContractViewer contract={selected} />
                </>
            )}
        </div>
    );
};

export default StopLossContractDetail;
